import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Users, FileText, TrendingUp, Search,
  ChevronLeft, ChevronRight, Crown, Zap, Star,
  Lock, Eye, EyeOff, Trash2, RefreshCw, Mail,
  MailX, MessageSquare, AlertTriangle, X
} from 'lucide-react';

import { BACKEND_URL } from '../config';
const API = `${BACKEND_URL}/api`;
const ADMIN_SECRET = 'crackcuet_admin_2025';

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [secretInput, setSecretInput] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [inquiries, setInquiries] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const limit = 20;

  const authHeader = { Authorization: `Admin ${ADMIN_SECRET}` };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/stats`, { headers: authHeader });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/admin/users?skip=${page * limit}&limit=${limit}${search ? `&search=${search}` : ''}`;
      const res = await fetch(url, { headers: authHeader });
      const data = await res.json();
      if (data.success) { setUsers(data.data.users); setTotalUsers(data.data.total); }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [page, search]);

  const fetchInquiries = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/inquiries`, { headers: authHeader });
      const data = await res.json();
      if (data.success) setInquiries(data.data.inquiries);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    if (authenticated) {
      fetchStats();
      fetchUsers();
    }
  }, [authenticated, page, search, fetchStats, fetchUsers]);

  useEffect(() => {
    if (authenticated && activeTab === 'inquiries') fetchInquiries();
  }, [authenticated, activeTab, fetchInquiries]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (secretInput === ADMIN_SECRET) setAuthenticated(true);
    else alert('Invalid admin secret');
  };

  const fetchUserDetails = async (userId) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/details`, { headers: authHeader });
      const data = await res.json();
      if (data.success) { setUserDetails(data.data); setSelectedUser(userId); }
    } catch (err) { console.error(err); }
  };

  const updateUserPlan = async (userId, newPlan) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: 'PATCH',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: newPlan })
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers(); fetchStats();
        if (selectedUser === userId) fetchUserDetails(userId);
        showToast(`Plan updated to ${newPlan}`);
      }
    } catch (err) { console.error(err); }
    setActionLoading(false);
  };

  const toggleEmailSequence = async (userId, stopped) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: 'PATCH',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_sequence_stopped: stopped })
      });
      const data = await res.json();
      if (data.success) {
        if (selectedUser === userId) fetchUserDetails(userId);
        showToast(stopped ? 'Email sequence stopped' : 'Email sequence resumed');
      }
    } catch (err) { console.error(err); }
    setActionLoading(false);
  };

  const resetMockCount = async (userId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/users/${userId}/reset-mocks`, {
        method: 'POST',
        headers: authHeader
      });
      const data = await res.json();
      if (data.success) {
        if (selectedUser === userId) fetchUserDetails(userId);
        showToast('Mock count reset to zero');
      }
    } catch (err) { console.error(err); }
    setActionLoading(false);
  };

  const deleteUser = async (userId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: authHeader
      });
      const data = await res.json();
      if (data.success) {
        setSelectedUser(null);
        setUserDetails(null);
        setConfirmDelete(null);
        fetchUsers();
        fetchStats();
        showToast('User deleted successfully');
      } else {
        showToast(data.message || 'Delete failed', 'error');
      }
    } catch (err) { showToast('Delete failed', 'error'); }
    setActionLoading(false);
  };

  const getPlanBadge = (plan) => {
    if (plan === 'premium') return <Badge className="bg-amber-100 text-amber-700"><Crown className="h-3 w-3 mr-1" />Premium</Badge>;
    if (plan === 'pro') return <Badge className="bg-blue-100 text-blue-700"><Zap className="h-3 w-3 mr-1" />Pro</Badge>;
    return <Badge className="bg-gray-100 text-gray-600"><Star className="h-3 w-3 mr-1" />Free</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle>Admin Access</CardTitle>
            <p className="text-sm text-gray-500 mt-2">Enter admin secret to continue</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={secretInput}
                  onChange={(e) => setSecretInput(e.target.value)}
                  placeholder="Admin Secret"
                  className="pr-10"
                />
                <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button type="submit" className="w-full">Enter Dashboard</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-lg text-white font-medium text-sm flex items-center gap-2 ${toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
          {toast.msg}
          <button onClick={() => setToast(null)}><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-2 rounded-full">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Delete User</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              This will permanently delete <strong>{confirmDelete.email}</strong> and all their mock test data. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmDelete(null)} disabled={actionLoading}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={() => deleteUser(confirmDelete.id)} disabled={actionLoading}>
                {actionLoading ? 'Deleting...' : 'Delete User'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <img src="/logo-full.png" alt="Crack CUET" className="h-16" />
            <Badge variant="outline" className="text-blue-600 border-blue-200">Admin</Badge>
          </div>
          <Button variant="outline" size="sm" onClick={() => setAuthenticated(false)}>Logout</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2.5 rounded-lg"><Users className="h-5 w-5 text-blue-600" /></div>
                  <div><p className="text-2xl font-bold">{stats.total_users}</p><p className="text-xs text-gray-500">Total Users</p></div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-2.5 rounded-lg"><TrendingUp className="h-5 w-5 text-green-600" /></div>
                  <div><p className="text-2xl font-bold">{stats.today_signups}</p><p className="text-xs text-gray-500">Today Signups</p></div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-violet-100 p-2.5 rounded-lg"><TrendingUp className="h-5 w-5 text-violet-600" /></div>
                  <div><p className="text-2xl font-bold">{stats.recent_signups}</p><p className="text-xs text-gray-500">Last 7 Days</p></div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-100 p-2.5 rounded-lg"><FileText className="h-5 w-5 text-purple-600" /></div>
                  <div><p className="text-2xl font-bold">{stats.total_attempts}</p><p className="text-xs text-gray-500">Total Attempts</p></div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 pb-4">
                <p className="text-xs text-gray-500 mb-2">Plan Distribution</p>
                <div className="flex flex-wrap gap-1.5">
                  <Badge className="bg-gray-100 text-gray-600 text-xs">{stats.plan_distribution.free} Free</Badge>
                  <Badge className="bg-blue-100 text-blue-600 text-xs">{stats.plan_distribution.pro} Pro</Badge>
                  <Badge className="bg-amber-100 text-amber-600 text-xs">{stats.plan_distribution.premium} Prem</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-200 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === 'users' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <Users className="h-4 w-4 inline mr-2" />Users
          </button>
          <button
            onClick={() => setActiveTab('inquiries')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === 'inquiries' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <MessageSquare className="h-4 w-4 inline mr-2" />Contact Inquiries
          </button>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Users List */}
            <Card className="md:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Users ({totalUsers})</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by email or name..."
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                      className="pl-9"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8 text-gray-500">Loading...</p>
                ) : (
                  <>
                    <div className="space-y-2">
                      {users.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => fetchUserDetails(user.id)}
                          className={`p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition ${selectedUser === user.id ? 'border-blue-500 bg-blue-50' : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{user.name || user.email}</p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {getPlanBadge(user.plan)}
                              <span className="text-xs text-gray-400">{user.attempt_count} attempts</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                        <ChevronLeft className="h-4 w-4" /> Prev
                      </Button>
                      <span className="text-sm text-gray-500">Page {page + 1} of {Math.max(1, Math.ceil(totalUsers / limit))}</span>
                      <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= totalUsers}>
                        Next <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* User Details Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">User Details</CardTitle>
              </CardHeader>
              <CardContent>
                {userDetails ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="font-medium">{userDetails.user.name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-medium text-sm">{userDetails.user.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Plan</p>
                      <div className="mt-1">{getPlanBadge(userDetails.user.plan)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">Joined</p>
                        <p className="font-medium text-sm">{formatDate(userDetails.user.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Mocks Taken</p>
                        <p className="font-medium text-sm">{userDetails.user.total_mocks_taken || 0}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Subjects</p>
                      <p className="font-medium text-sm">{userDetails.user.subjects?.join(', ') || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Email Sequence</p>
                      <Badge className={userDetails.user.email_sequence_stopped ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                        {userDetails.user.email_sequence_stopped ? 'Stopped' : `Active (Day ${userDetails.user.email_sequence_day || 0})`}
                      </Badge>
                    </div>

                    {/* Change Plan */}
                    <div className="pt-3 border-t">
                      <p className="text-xs text-gray-500 mb-2">Change Plan</p>
                      <div className="flex gap-2">
                        {['free', 'pro', 'premium'].map(p => (
                          <Button
                            key={p}
                            size="sm"
                            variant={userDetails.user.plan === p ? 'default' : 'outline'}
                            onClick={() => updateUserPlan(userDetails.user.id, p)}
                            disabled={userDetails.user.plan === p || actionLoading}
                            className="capitalize flex-1 text-xs"
                          >{p}</Button>
                        ))}
                      </div>
                    </div>

                    {/* Admin Actions */}
                    <div className="pt-3 border-t space-y-2">
                      <p className="text-xs text-gray-500 mb-2">Admin Actions</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full justify-start text-xs"
                        onClick={() => resetMockCount(userDetails.user.id)}
                        disabled={actionLoading}
                      >
                        <RefreshCw className="h-3.5 w-3.5 mr-2 text-blue-500" />Reset Monthly Mock Count
                      </Button>
                      {userDetails.user.email_sequence_stopped ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full justify-start text-xs"
                          onClick={() => toggleEmailSequence(userDetails.user.id, false)}
                          disabled={actionLoading}
                        >
                          <Mail className="h-3.5 w-3.5 mr-2 text-green-500" />Resume Email Sequence
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full justify-start text-xs"
                          onClick={() => toggleEmailSequence(userDetails.user.id, true)}
                          disabled={actionLoading}
                        >
                          <MailX className="h-3.5 w-3.5 mr-2 text-orange-500" />Stop Email Sequence
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="w-full justify-start text-xs bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                        variant="outline"
                        onClick={() => setConfirmDelete({ id: userDetails.user.id, email: userDetails.user.email })}
                        disabled={actionLoading}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" />Delete User
                      </Button>
                    </div>

                    {/* Recent Attempts */}
                    {userDetails.recent_attempts?.length > 0 && (
                      <div className="pt-3 border-t">
                        <p className="text-xs text-gray-500 mb-2">Recent Attempts</p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {userDetails.recent_attempts.slice(0, 5).map((attempt, i) => (
                            <div key={i} className="text-sm p-2 bg-gray-50 rounded">
                              <div className="flex justify-between">
                                <span className="font-medium text-xs">{attempt.subject}</span>
                                <span className="text-green-600 text-xs font-semibold">
                                  {attempt.score} pts · {attempt.percentile ?? 0}%ile
                                </span>
                              </div>
                              <p className="text-gray-400 text-xs">{formatDate(attempt.submitted_at)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-8 text-sm">Select a user to view details</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Contact Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <Card>
            <CardHeader>
              <CardTitle>Contact Inquiries ({inquiries.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {inquiries.length === 0 ? (
                <p className="text-center text-gray-400 py-10">No inquiries yet</p>
              ) : (
                <div className="space-y-4">
                  {inquiries.map((inq) => (
                    <div key={inq.id} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold">{inq.name}</p>
                          <p className="text-sm text-gray-500">{inq.email}{inq.phone ? ` · ${inq.phone}` : ''}</p>
                        </div>
                        <span className="text-xs text-gray-400">{formatDate(inq.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{inq.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
